// Angular
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgClass, Location } from '@angular/common';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
  AsyncValidatorFn,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Angular Material
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';

// Third Party
import { merge, Subject, forkJoin, of, Observable, delay, map, switchMap, finalize } from 'rxjs';

// Services
import { IntegrationService } from '@services/integration.service';
import { CustomerService } from '@services/customer.service';

// Defs
import {
  CustomerInfo,
  Integration,
  DEFAULT_API_KEY_HEADER_NAME,
  OPERATIONAL_META_DATA,
  TIME_OPTIONS,
  DISTANCE_OPTIONS,
  IntegrationTypeMetaData,
  INTEGRATION_CATEGORY_LIST,
  ObjectStrategy,
  AuthType,
} from '@components/integration';
import { Active, Action, GrantType, IntegrationProperty, AuthTypeEnum } from '@constants/enum';
import { DEBOUNCE_TIME_SEARCH_FIELDS } from '@constants/common';

// Dialogs
import { SuccessDialogComponent, WarningDialogComponent } from '@components/dialogs';

// Components
import { LoadingSpinnerComponent } from '@components/common-components';

// Util
import { httpUrlValidator } from '@utils/custom-validators';

@Component({
  selector: 'app-integration-form',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCardModule,
    MatSidenavModule,
    NgClass,
    LoadingSpinnerComponent,
  ],
  templateUrl: './integration-form.component.html',
  styleUrl: './integration-form.component.scss',
})
export class IntegrationFormComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private integrationService = inject(IntegrationService);
  private customerService = inject(CustomerService);
  private matDialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private location = inject(Location);

  // Page Type
  readonly pageType = signal<string>(null); // Create / Edit
  readonly pageNameTitle = signal('');

  // Enums
  readonly action = Action;

  // Constants
  readonly timeOptions = TIME_OPTIONS;
  readonly distanceOptions = DISTANCE_OPTIONS;

  // Errors and loading states
  readonly loading = signal(true);
  readonly formPrepareError = signal(false);

  // Integrations & Form
  integration: Integration;

  readonly integrationForm = new FormGroup({
    apiKey: new FormControl(''),
    apiKeyHeaderName: new FormControl(DEFAULT_API_KEY_HEADER_NAME),
    authType: new FormControl(null as AuthType, [Validators.required]),
    authEndpointUrl: new FormControl('', [httpUrlValidator()]),
    authPassword: new FormControl(''),
    authUsername: new FormControl(''),
    contactEmail: new FormControl('', [Validators.email]),
    contactName: new FormControl(''),
    contactPhone: new FormControl('', [Validators.pattern(/^\+?[0-9\s\-()]+$/)]),
    customers: new FormControl([] as string[], [Validators.required]),
    distanceMeasurement: new FormControl(''),
    enabled: new FormControl(Active.active, [Validators.required]),
    id: new FormControl(null as number),
    integrationType: new FormControl(OPERATIONAL_META_DATA.CAD),
    oauthClientId: new FormControl(''),
    oauthClientSecret: new FormControl(''),
    oauthGrantType: new FormControl(''),
    pushUrl: new FormControl('', [Validators.required, httpUrlValidator()]),
    objectStrategy: new FormControl(null),
    timeMeasurement: new FormControl(''),
    vendorKey: new FormControl(
      '',
      [Validators.required],
      this.uniqueValidator(IntegrationProperty.vendorKey),
    ),
    vendorName: new FormControl(
      '',
      [Validators.required],
      this.uniqueValidator(IntegrationProperty.vendorName),
    ),
  });

  readonly subjReload = new Subject<void>();

  // Form Dropdown Values
  readonly customers = signal<CustomerInfo[]>([]);
  readonly authTypes = signal<AuthType[]>([]);
  readonly integrationTypes = signal<IntegrationTypeMetaData[]>([]);
  readonly objectStrategies = signal<ObjectStrategy[]>([]);

  readonly enabledList: string[] = Object.values(Active);
  readonly grantType: string[] = Object.values(GrantType);

  // Input field focus
  readonly focusState = signal<Record<string, boolean>>({});
  readonly apiDetails = signal<IntegrationTypeMetaData>(OPERATIONAL_META_DATA.CAD);

  // Customer filter
  readonly customerFilter = signal('');

  ngOnInit() {
    this.setIntegrationType(this.route.snapshot.params['integrationType']);

    if (this.router.url.includes(Action.edit)) {
      this.pageType.set(Action.edit);
      this.integrationForm.get('integrationType').disable();
      const id = this.route.snapshot.params['id'];
      this.getIntegrationById(id);
    } else {
      this.pageType.set(Action.create);
      this.prepareForm();
    }
    this.changeApiType();
  }

  getIntegrationById(id: string) {
    this.loading.set(true);
    this.integrationService
      .getIntegrationById(id)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (data) => {
          this.integration = data as Integration;
          this.pageNameTitle.set(this.integration.vendorName);
          this.integrationForm.patchValue({
            ...this.integration,
            apiKeyHeaderName: this.integration.apiKeyHeaderName || DEFAULT_API_KEY_HEADER_NAME,
            contactEmail: this.integration.contactInformation.contactEmail,
            contactName: this.integration.contactInformation.contactName,
            contactPhone: this.integration.contactInformation.contactPhone,
            customers: this.integration.customers.map((c) => c.customerKey),
            enabled: this.integration.enabled ? Active.active : Active.inactive,
            distanceMeasurement: this.integration.metrics?.distance,
            timeMeasurement: this.integration.metrics?.time,
            integrationType: Object.values(INTEGRATION_CATEGORY_LIST)
              .filter((category) => category.metaData)
              .flatMap((category) => Object.values(category.metaData))
              .find((type) => type.queryParam === this.integration.integrationType),
          });
          this.prepareForm();
        },
        error: () => this.formPrepareError.set(true),
      });
  }

  onSubmit(form: FormGroup) {
    if (form.valid) {
      const {
        contactEmail,
        contactName,
        contactPhone,
        distanceMeasurement,
        timeMeasurement,
        objectStrategy,
        integrationType,
        ...rest
      } = form.getRawValue();

      const integration: Integration = {
        ...rest,
        contactInformation: {
          contactEmail,
          contactName,
          contactPhone,
        },
        customers: this.generateCustomerInfo(rest.customers),
        enabled: rest.enabled === Active.active,
        metrics: {
          id: this.pageType() === Action.edit ? this.integration.metrics?.id : null,
          distance: distanceMeasurement,
          time: timeMeasurement,
        },
        objectStrategy: objectStrategy,
        integrationType: integrationType.queryParam,
      };
      this.openAreYouSureDialog(integration.vendorName, integration.customers)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (confirmed) => {
            if (confirmed) {
              this.loading.set(true);
              const operation =
                this.pageType() === Action.create
                  ? this.integrationService.saveIntegration(integration)
                  : this.integrationService.updateIntegration(integration);

              operation.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: () => {
                  this.loading.set(false);
                  this.openSuccessDialog(this.pageType());
                },
                error: (error) => {
                  this.loading.set(false);
                  console.error('Operation failed:', error);
                },
              });
            }
          },
        });
    } else {
      form.markAllAsTouched();
    }
  }

  setIntegrationType(integrationType: string) {
    const key = integrationType.toUpperCase();
    const category = INTEGRATION_CATEGORY_LIST[key];
    this.integrationTypes.set(Object.values(category.metaData));
  }

  uniqueValidator(propertyName: string): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      // If shared integration exists - it's an edit
      if (this.integration) {
        switch (propertyName) {
          case IntegrationProperty.vendorName: {
            // If the starting edit value equals current value - no error needed
            if (control.value === this.integration.vendorName) {
              return of(null);
            }
            break;
          }
          case IntegrationProperty.vendorKey: {
            // If the starting edit value equals current value - no error needed
            if (control.value === this.integration.vendorKey) {
              return of(null);
            }
            break;
          }
          default: {
            // If the uniqueness statement cannot be verified - error
            console.error('Case not found for unique switch statement');
            return of({ isNotUnique: true });
          }
        }
      }
      return of(control.value).pipe(
        delay(DEBOUNCE_TIME_SEARCH_FIELDS),
        switchMap((value) => {
          if (!value) {
            // No validation error if the value is empty
            return of(null);
          }
          return this.integrationService.isValueUnique(propertyName, value).pipe(
            map((isResultUnique) => {
              return isResultUnique ? null : { isNotUnique: true };
            }),
          );
        }),
      );
    };
  }

  prepareForm() {
    // Set validators based on integration type
    if (this.apiDetails() === OPERATIONAL_META_DATA.FLEET) {
      this.integrationForm.get('distanceMeasurement').setValidators([Validators.required]);
      this.integrationForm.get('timeMeasurement').setValidators([Validators.required]);
      this.integrationForm.get('objectStrategy').setValidators([Validators.required]);

      ['distanceMeasurement', 'timeMeasurement', 'objectStrategy'].forEach((control) => {
        this.integrationForm.get(control).updateValueAndValidity();
      });
    }
    merge(this.subjReload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          return forkJoin({
            customers: this.customerService.getOperationalApiEnabledCustomers(
              this.apiDetails()?.queryParam,
            ),
            authTypes: this.integrationService.getAuthTypes(),
            objectStrategies: this.integrationService.getObjectStrategies(
              this.apiDetails()?.queryParam,
            ),
          });
        }),
      )
      .subscribe({
        next: ({ customers, authTypes, objectStrategies }) => {
          if (customers) {
            this.customers.set(customers as CustomerInfo[]);
          }
          if (authTypes) {
            this.authTypes.set(authTypes as AuthType[]);
            const areEnumsValid = this.validateAuthEnums(this.authTypes());

            this.formPrepareError.set(this.authTypes().length === 0 || !areEnumsValid);
            if (this.formPrepareError()) {
              console.error('Default values were not found');
              this.loading.set(false);
              this.formPrepareError.set(true);
            }

            if (this.pageType() === this.action.create) {
              this.integrationForm.get('authType').setValue(this.authTypes()[0]);
            } else {
              this.integrationForm
                .get('authType')
                .setValue(
                  this.authTypes().find(
                    (authType) => authType.authMethod === this.integration.authType.authMethod,
                  ),
                );
            }

            this.changeValidators(this.integrationForm.get('authType').value);
          }
          if (objectStrategies) {
            this.objectStrategies.set(objectStrategies as ObjectStrategy[]);

            // Re-apply objectStrategy now that options are available
            if (this.pageType() === this.action.edit && this.integration?.objectStrategy) {
              this.integrationForm.get('objectStrategy').setValue(this.integration.objectStrategy);
            }
          }

          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.formPrepareError.set(true);
        },
      });
    this.subjReload.next();
  }

  changeApiType() {
    this.integrationForm
      .get('integrationType')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedType: IntegrationTypeMetaData) => {
        if (selectedType) {
          this.apiDetails.set(selectedType);

          // Fetch new object strategy types
          this.integrationService
            .getObjectStrategies(selectedType.queryParam)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (objectStrategies) => {
                this.objectStrategies.set(objectStrategies as ObjectStrategy[]);
                this.integrationForm.get('objectStrategy').setValue(null);

                if (selectedType === OPERATIONAL_META_DATA.FLEET) {
                  this.integrationForm
                    .get('distanceMeasurement')
                    .setValidators([Validators.required]);
                  this.integrationForm.get('timeMeasurement').setValidators([Validators.required]);
                  this.integrationForm.get('objectStrategy').setValidators([Validators.required]);
                } else {
                  this.integrationForm.get('distanceMeasurement').clearValidators();
                  this.integrationForm.get('timeMeasurement').clearValidators();
                  this.integrationForm.get('objectStrategy').clearValidators();
                }
                ['distanceMeasurement', 'timeMeasurement', 'objectStrategy'].forEach((control) => {
                  this.integrationForm.get(control).updateValueAndValidity();
                });
              },
              error: (err) => console.error('Failed to fetch object strategy types', err),
            });

          // Get new customers for selected api type
          this.customerService
            .getOperationalApiEnabledCustomers(selectedType.queryParam)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (customers) => {
                if (!customers) {
                  this.customers.set([]);
                  this.integrationForm.get('customers').setValue([]);
                  return;
                }

                this.customers.set(customers as CustomerInfo[]);
                // Clear any previously selected customers that are no longer valid
                const validKeys = (customers as CustomerInfo[]).map((c) => c.customerKey);
                const currentSelected = this.integrationForm.get('customers').value;
                const filtered = currentSelected.filter((key) => validKeys.includes(key));
                this.integrationForm.get('customers').setValue(filtered);
              },
              error: (err) => console.error('Failed to fetch customers', err),
            });
        }
      });
  }

  removeCustomer(name: string) {
    this.integrationForm
      .get('customers')
      .setValue(this.integrationForm.get('customers').value.filter((cus) => cus !== name));
  }

  generateCustomerInfo(customers: string[]) {
    const customerInfo: CustomerInfo[] = [];
    customers.forEach((customer) => {
      this.customers().forEach((info) => {
        if (info.customerKey === customer) {
          customerInfo.push(info);
        }
      });
    });
    return customerInfo;
  }

  private readonly errorMessages: Record<string, { error: string; message: string }[]> = {
    vendorName: [
      { error: 'isNotUnique', message: 'A vendor already exists with that name.' },
      { error: 'required', message: 'You must enter a vendor name.' },
    ],
    vendorKey: [
      { error: 'isNotUnique', message: 'A vendor already exists with that key.' },
      { error: 'required', message: 'You must enter a vendor key.' },
    ],
    pushUrl: [{ error: 'required', message: 'You must enter a valid endpoint.' }],
    customers: [{ error: 'required', message: 'You must select at least one customer.' }],
    authType: [{ error: 'required', message: 'You must select an authentication type.' }],
    apiKey: [{ error: 'required', message: 'You must enter an api key.' }],
    apiKeyHeaderName: [{ error: 'required', message: 'You must enter an api key header name.' }],
    contactEmail: [{ error: 'email', message: 'You must enter a valid email.' }],
    authEndpointUrl: [
      { error: 'required', message: 'You must enter a valid authentication endpoint.' },
    ],
    authUsername: [
      { error: 'required', message: 'You must enter a valid authentication username.' },
    ],
    authPassword: [
      { error: 'required', message: 'You must enter a valid authentication password.' },
    ],
    oauthClientId: [{ error: 'required', message: 'You must enter a valid Client ID.' }],
    oauthClientSecret: [{ error: 'required', message: 'You must enter a valid Client Secret.' }],
    oauthGrantType: [{ error: 'required', message: 'You must select a Grant Type.' }],
    objectStrategy: [{ error: 'required', message: 'You must select a Response Model.' }],
    distanceMeasurement: [
      { error: 'required', message: 'You must select a Distance Measurement.' },
    ],
    timeMeasurement: [{ error: 'required', message: 'You must select a Time Measurement.' }],
  };

  getErrorMessage(field: string): string | null {
    const control = this.integrationForm.get(field);
    const messages = this.errorMessages[field];

    if (!control || !messages) return null;

    for (const { error, message } of messages) {
      if (control.hasError(error) && (error === 'isNotUnique' || control.touched)) {
        return message;
      }
    }

    return null;
  }

  openSuccessDialog(pageType: string) {
    const updatedType = pageType == Action.create ? 'created' : 'updated';
    this.matDialog.open(SuccessDialogComponent, {
      data: {
        title: `Integration ${updatedType}!`,
        message: `You have successfully ${updatedType} an ACETECH™ Integration.`,
        button: 'Great!',
      },
    });
    this.location.back();
  }

  openWarningDialog() {
    const dialogRef = this.matDialog.open(WarningDialogComponent, {
      data: {
        title: 'Are you sure!',
        message: 'Please confirm you wish to discard current changes made.',
        buttonConfirm: 'Yes, Cancel',
        buttonCancel: 'Keep Editing',
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (data) {
          this.location.back();
        }
      });
  }

  openAreYouSureDialog(vendor: string, customersInfo: CustomerInfo[]) {
    const customers: string[] = customersInfo.map((customer) => customer.customerKey);
    const dialogRef = this.matDialog.open(WarningDialogComponent, {
      data: {
        title: 'Are you sure!',
        message: `Please confirm you wish to associate the vendor:\n<b> ${vendor}</b>\nWith the following customers:\n<b> ${customers}</b>`,
        buttonConfirm: this.pageType() == Action.edit ? 'Yes, Update!' : 'Yes, Create!',
        buttonCancel: 'Keep Editing',
      },
    });
    return dialogRef.afterClosed();
  }

  // Used to prevent errors appearing too soon
  onInputFocus(formValue: string) {
    this.focusState.update((state) => ({ ...state, [formValue]: true }));
  }

  onInputBlur(formValue: string) {
    this.focusState.update((state) => ({ ...state, [formValue]: false }));
  }

  changeValidators(authType: AuthType) {
    const coreControls: string[] = ['vendorName', 'vendorKey', 'pushUrl', 'customers'];

    // Clear existing errors for core fields for Auth Type change
    coreControls.forEach((control) => {
      this.integrationForm.get(control).markAsUntouched();
    });

    const optionalControls: string[] = [
      'authEndpointUrl',
      'authPassword',
      'authUsername',
      'apiKey',
      'apiKeyHeaderName',
      'oauthClientId',
      'oauthClientSecret',
      'oauthGrantType',
    ];

    // Clear existing validators for optional fields
    optionalControls.forEach((control) => {
      this.integrationForm.get(control).clearValidators();
      this.integrationForm.get(control).markAsUntouched();
    });

    // Set validators based on authentication type selected
    switch (authType.authMethod) {
      case AuthTypeEnum.apikey:
        this.integrationForm.get('apiKey').setValidators([Validators.required]);
        this.integrationForm.get('apiKeyHeaderName').setValidators([Validators.required]);
        break;
      case AuthTypeEnum.jwt:
        this.integrationForm.get('authEndpointUrl').setValidators([Validators.required]);
        this.integrationForm.get('authPassword').setValidators([Validators.required]);
        this.integrationForm.get('authUsername').setValidators([Validators.required]);
        break;
      case AuthTypeEnum.oauth:
        this.integrationForm.get('authEndpointUrl').setValidators([Validators.required]);
        this.integrationForm.get('oauthClientId').setValidators([Validators.required]);
        this.integrationForm.get('oauthClientSecret').setValidators([Validators.required]);
        this.integrationForm.get('oauthGrantType').setValidators([Validators.required]);
        break;
      default:
        console.error('Case not found for changeValidators() : ' + authType.authMethod);
    }
    // Update value and validity after setting new validators
    optionalControls.forEach((control) => {
      this.integrationForm.get(control).updateValueAndValidity();
    });
  }

  validateAuthEnums(authTypes: AuthType[]): boolean {
    // Additional check to ensure ENUM matches DB Value
    // Easier to refer to the ENUMS then looking for a specific value each time
    let isValid = true;
    if (authTypes[0] == null || authTypes[0].authMethod !== AuthTypeEnum.apikey) {
      console.error('API Key Enum mismatch');
      isValid = false;
    }
    if (authTypes[1] == null || authTypes[1].authMethod !== AuthTypeEnum.jwt) {
      console.error('JWT Enum mismatch');
      isValid = false;
    }
    if (authTypes[2] == null || authTypes[2].authMethod !== AuthTypeEnum.oauth) {
      console.error('OAUTH Enum mismatch');
      isValid = false;
    }
    return isValid;
  }

  get isApiKeyAuth(): boolean {
    return this.integrationForm.get('authType')?.value?.authMethod === AuthTypeEnum.apikey;
  }

  get isJwtAuth(): boolean {
    return this.integrationForm.get('authType')?.value?.authMethod === AuthTypeEnum.jwt;
  }

  get isOauthAuth(): boolean {
    return this.integrationForm.get('authType')?.value?.authMethod === AuthTypeEnum.oauth;
  }

  get isFleetApi(): boolean {
    return this.integrationForm.get('integrationType')?.value === OPERATIONAL_META_DATA.FLEET;
  }

  get getAssociatedCustomers(): string[] {
    return this.integrationForm.get('customers').value;
  }

  get filteredAssociatedCustomers(): string[] {
    const filter = this.customerFilter().toLowerCase();
    if (!filter) return this.getAssociatedCustomers;
    return this.getAssociatedCustomers.filter((cus) => cus.toLowerCase().includes(filter));
  }
}
